import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { Box, Button, Divider } from "@mui/material";
import { NetworkCheck, Place, Public, SignalCellularAlt } from "@mui/icons-material";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { isEqual } from "lodash";
import { mapValues, mapValuesToSaveData } from "../../utils";
import allRoutes from "../../constants/routes";
import {
  initialValues,
  selectOperatorsSlice,
  setSavedValues,
  setValues as setOperatorsValues,
  setSubRegions,
  setAllSubRegions,
} from "../../store/slices/operators";
import { closeOperatorsFilters, selectUiState, setFiltersOpen, setOperatorsFilters } from "../../store/slices/ui";
import {
  useGetRegionsQuery,
  useGetChildRegionsQuery,
  useGetCountriesQuery,
  useGetSunsetTechsQuery,
  useGetUpgradeTechsQuery,
  useGetItuRegionsQuery,
  useGetFeaturesQuery,
} from "../../store/apis/search";
import { useSaveSearchMutation } from "../../store/apis/save";
import FilterList from "../FilterList";
import SearchFilter from "../SearchFilter";
import RadioFilter from "../RadioFilter";
import { privateOptions } from "../../constants";
import YearRangeFilter from "../YearRangeFilter";
import CheckboxFilter from "../CheckboxFilter";
import colors from "../../theme/colors";

const OperatorsForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { values: valuesCurrent, lastSaved, loading, filteredSubRegions } = useSelector(selectOperatorsSlice);
  const { operators: filters } = useSelector(selectUiState);

  const { values, setFieldValue, setValues, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: values => {
      dispatch(setFiltersOpen(false));
      dispatch(setOperatorsValues(values));

      const { path } = allRoutes.operators;

      if (pathname !== `${path}/search`) {
        navigate(`${path}/search`);
      }
    },
  });

  const { data: featuresOptions = [], isLoading: isFeaturesLoading } = useGetFeaturesQuery();
  const { data: regionsOptions = [], isLoading: regionsLoading } = useGetRegionsQuery();
  const { data: switchoffTechnologiesOptions = [], isLoading: isSunsetTechnologiesLoading } = useGetSunsetTechsQuery();
  const { data: upgradeTechnologiesOptions = [], isLoading: isUpgradeTechnologiesLoading } = useGetUpgradeTechsQuery();
  const { data: childRegionOptions = [], isLoading: isChildRegionsLoading } = useGetChildRegionsQuery();
  const { data: countriesOptions = [], isLoading: countriesLoading } = useGetCountriesQuery({
    ituRegions: [],
    regions: values.regions,
  });
  const { data: ituRegionsOptions = [], isLoading: isItuRegionsLoading } = useGetItuRegionsQuery();

  useEffect(() => {
    setValues(valuesCurrent);
  }, [setValues, valuesCurrent]);

  useEffect(() => {
    if (!isChildRegionsLoading) {
      dispatch(setAllSubRegions(childRegionOptions));
      dispatch(setSubRegions([]));
    }
  }, [childRegionOptions, dispatch, isChildRegionsLoading]);

  const setFilterOpen = useCallback(
    (key: string, value: boolean) => {
      dispatch(setOperatorsFilters({ ...filters, [key]: value }));
    },
    [dispatch, filters],
  );

  const [saveSearch] = useSaveSearchMutation();

  const handleSave = (title: string) => {
    dispatch(setSavedValues(valuesCurrent));
    saveSearch({
      sectionId: 4,
      data: { title, ...mapValuesToSaveData(4, valuesCurrent) },
    });
  };

  return (
    <FilterList
      loading={loading}
      disabled={regionsLoading || countriesLoading}
      canSearch={!isEqual(values, valuesCurrent)}
      canSave={!isEqual(valuesCurrent, lastSaved)}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onSave={handleSave}
    >
      <Box paddingX={8} paddingY={4} textAlign="right">
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            dispatch(closeOperatorsFilters());
          }}
          style={{ backgroundColor: colors.white }}
        >
          Close all
        </Button>
      </Box>
      <Divider />
      <SearchFilter
        title="Search by name"
        name="operators-search"
        tooltip="Filter the search by Operator name"
        value={values.search}
        options={[]}
        loading={false}
        disabled={false}
        open={filters.search}
        noAutocomplete={true}
        onChange={() => { }}
        onTextChange={value => {
          setFieldValue("search", value);
        }}
        setOpen={value => setFilterOpen("search", value)}
      />
      <SearchFilter
        title="Features"
        name="features-filter"
        tooltip="Network features supported"
        value={featuresOptions.filter(option => values.features.includes(option.id))}
        icon={<NetworkCheck fontSize="small" />}
        options={featuresOptions}
        loading={isFeaturesLoading}
        disabled={!featuresOptions.length}
        open={filters.features}
        setOr={value => setFieldValue("featuresControl", value)}
        orValue={values.featuresControl}
        onChange={value => setFieldValue("features", mapValues(value))}
        setOpen={value => setFilterOpen("features", value)}
      />
      <SearchFilter
        title="ITU Regions"
        name="itu-regions-filter"
        tooltip="Filter the search by ITU Region"
        value={ituRegionsOptions.filter(option => values.ituRegions.includes(option.id))}
        icon={<Public fontSize="small" />}
        options={ituRegionsOptions}
        loading={isItuRegionsLoading}
        disabled={!ituRegionsOptions.length}
        open={filters.ituRegions}
        onChange={value => {
          setFieldValue("ituRegions", mapValues(value));
          setFieldValue("regions", []);

          if (!value.length) {
            setFieldValue("countries", []);
          }
        }}
        setOpen={value => setFilterOpen("ituRegions", value)}
      />
      <SearchFilter
        title="Main Regions"
        name="operators-regions"
        tooltip="Filter the search by ITU Region"
        value={regionsOptions.filter(option => values.regions.includes(option.id))}
        icon={<Public fontSize="small" />}
        options={regionsOptions}
        loading={regionsLoading}
        disabled={!regionsOptions.length}
        open={filters.regions}
        onChange={value => {
          dispatch(setSubRegions(value.map(item => item.id)));
          setFieldValue("regions", mapValues(value));
        }}
        setOpen={value => setFilterOpen("regions", value)}
      />
      <SearchFilter
        title="Sub Regions"
        name="regions-filter"
        tooltip="Filter the search by Region"
        value={filteredSubRegions.filter(option => values.regions.includes(option.id))}
        icon={<Public fontSize="small" />}
        options={filteredSubRegions}
        loading={isChildRegionsLoading}
        disabled={!filteredSubRegions.length}
        open={filters.childRegions}
        onChange={value => {
          setFieldValue("ituRegions", []);
          setFieldValue("childRegions", mapValues(value));
          setFieldValue("regions", mapValues(value));

          if (!value.length) {
            setFieldValue("countries", []);
          }
        }}
        setOpen={value => setFilterOpen("childRegions", value)}
      />
      <SearchFilter
        title="Country/Territories"
        name="operators-countries"
        tooltip="Filter by specific country"
        value={countriesOptions.filter(option => values.countries.includes(option.id))}
        icon={<Place fontSize="small" />}
        options={countriesOptions}
        loading={countriesLoading}
        disabled={!countriesOptions.length}
        open={filters.countries}
        onChange={value => setFieldValue("countries", mapValues(value))}
        setOpen={value => setFilterOpen("countries", value)}
      />
      <RadioFilter
        title="Public Status"
        name="public-filter"
        tooltip="Filter by public status"
        value={privateOptions.find(option => option.id === values.publicStatus)}
        icon={<SignalCellularAlt fontSize="small" />}
        options={privateOptions}
        open={filters.publicStatus}
        onChange={value => setFieldValue("publicStatus", value?.id)}
        setOpen={value => setFilterOpen("publicStatus", value)}
      />
      <YearRangeFilter
        title="Switch Off Year"
        name="year-range-filter"
        tooltip="Search by year range. "
        value={[values.startDate, values.endDate]}
        open={filters.dates}
        onChange={(value: (number | null)[]) => {
          const [startDate, endDate] = value;

          setFieldValue("startDate", startDate);
          setFieldValue("endDate", endDate);
        }}
        setOpen={(open: boolean) => setFilterOpen("dates", open)}
      />
      <CheckboxFilter
        title="Switch Off Technology"
        tooltip="What Technology has been switchoffted."
        value={switchoffTechnologiesOptions.filter(option => values.switchoffTechs.includes(option.id))}
        icon={<PowerSettingsNewIcon fontSize="small" />}
        options={switchoffTechnologiesOptions}
        loading={isSunsetTechnologiesLoading}
        open={filters.switchoffTechs}
        onChange={value => setFieldValue("switchoffTechs", mapValues(value))}
        setOpen={value => setFilterOpen("switchoffTechs", value)}
        testId="switchoffFilter"
      />
      <CheckboxFilter
        title="Upgrade Technology"
        tooltip="What Technology has been upgraded to."
        value={upgradeTechnologiesOptions.filter(option => values.upgradeTechs.includes(option.id))}
        icon={<UpgradeIcon fontSize="small" />}
        options={upgradeTechnologiesOptions}
        loading={isUpgradeTechnologiesLoading}
        open={filters.upgradeTechs}
        onChange={value => setFieldValue("upgradeTechs", mapValues(value))}
        setOpen={value => setFilterOpen("upgradeTechs", value)}
        testId="upgradeFilter"
      />
    </FilterList>
  );
};

export default OperatorsForm;
